import AccommodationSameRoomOptionTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import AccommodationSameRoomOptionTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileDetails'
import AccommodationSameRoomOptionTilePricing from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTilePricing'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import React, { useCallback, useContext, useMemo } from 'react'
import BedbankCancellationPolicy from '../BedbankCancellationPolicy'
import BedbankPromotionDetails from '../BedbankPromotionDetails'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import WalledContent from 'components/Common/WalledContent'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import BedbankPackageTilePricing from '../BedbankPackageTilePricing'
import moment from 'moment'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import AccommodationSameRoomOptionTileBanner from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTileBanner'
import AccommodationOfferInclusionsExpandable from 'components/OfferPage/Accommodation/AccommodationOfferInclusionsExpandable'
import { selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import TextButton from 'LuxKit/Button/TextButton'
import useRoomOptionRateBedbankQuote from 'hooks/Insurance/useRoomOptionRateBedbankQuote'
import BedbankRoomOptionRateInsurance from '../BedbankRoomOptionRateInsurance'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'
import { rem } from 'polished'

interface Props {
  packageNumber: number
  rate: App.BedbankRate
  packageView: App.BedbankOfferPackageView
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  rooms: Array<OfferPageRoom>
  flightOrigin?: App.Airport
  rateListId?: string
  roomListId?: string
  isLoggedIn: boolean
  onBook: (rate: App.BedbankRate, ratePosition: number, withFlights?: boolean, keepBrowsing?: boolean) => any
  onAddToUserCart: (rate: App.BedbankRate, ratePosition: number, withFlights?: boolean) => any
  isFlexCfmrBedbankPackagesEnabled: boolean
}

function BedbankMultiSameRoomRoomOption(props: Props) {
  const {
    rate,
    packageNumber,
    packageView,
    checkInDate,
    checkOutDate,
    offer,
    rooms,
    flightOrigin,
    rateListId,
    roomListId,
    onBook,
    onAddToUserCart,
    isFlexCfmrBedbankPackagesEnabled,
  } = props

  const promotion = packageView.promotions[rate.id]

  const { bundleRates, payAtPropertyRates } = packageView
  const bundleRate = useMemo(() => bundleRates.find((bundleRate) => bundleRate.id === rate.id) ?? rate, [bundleRates, rate])

  const departureTooEarly = useMemo(() => {
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInDate])

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  const showModal = useContext(ModalContext)

  const onClickLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const { quote, isFetching: isQuoteFetching, error: quoteError, onSelectInsurance, isInsuranceSelected } = useRoomOptionRateBedbankQuote({
    country: offer.property.address.countryName,
    coverAmount: rate.totals.total,
    checkIn: checkInDate,
    checkout: checkOutDate,
    travellers: rooms,
    useCache: true,
    disabled: !isFlexCfmrBedbankPackagesEnabled,
  })

  const isRefundableRate = rate.refundable || rate.partiallyRefundable

  return <AccommodationSameRoomOptionTile>
    <ScrollAnchor id={`mobile-${rate.id}`} vOffset={rem(350)} />
    <AccommodationSameRoomOptionTileBanner
      packageNumber={packageNumber}
      promotion={promotion}
      tagLineLabel={<>
        {rate.corporate && <ProductTypeLabel productType="corporate" />}
        {promotion && <ProductTypeLabel productType="limited_time_special" />}
      </>}
    >
      {promotion && <BedbankPromotionDetails
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        promotion={promotion}
        rate={rate}
      />}
      {!promotion && <BedbankCancellationPolicy
        rate={rate}
        showNonCancellation={!isFlexCfmrBedbankPackagesEnabled}
      />}
    </AccommodationSameRoomOptionTileBanner>

    <AccommodationSameRoomOptionTileDetails>
      <AccommodationOfferInclusionsExpandable
        content={promotion?.rateInclusionsLong}
        inclusions={promotion?.inclusions ?? rate.inclusions}
        maxHeight={80}
        collapsedHeight={70}
      />
      {!isRefundableRate && isFlexCfmrBedbankPackagesEnabled && <div>
        <BedbankRoomOptionRateInsurance
          isLoading={isQuoteFetching}
          onChange={onSelectInsurance}
          errorOnQuote={!!quoteError}
          quote={quote}
          refundTitle="Full refund for unforeseen events"
          refundDetails="(Hotel only)"
        />
      </div>}
    </AccommodationSameRoomOptionTileDetails>

    <WalledContent
      enforced={offer.walledGarden}
      hidePricing={offer.hidePricing}
      wall={<TextButton
        kind="primary"
        size="large"
        onClick={onClickLogin}
        fit="full-width"
      >
        Log in to view prices
      </TextButton>}
    >
      <AccommodationSameRoomOptionTilePricing>
        <BedbankPackageTilePricing
          promotion={promotion}
          rate={rate}
          bundleRate={bundleRate}
          payAtPropertyRates={payAtPropertyRates}
          flightPrice={flightPrice}
          departureTooEarly={departureTooEarly}
          ratePosition={packageNumber}
          showPoints
          rateListId={rateListId}
          roomPosition={packageNumber}
          roomListId={roomListId}
          onBookNow={onBook}
          onAddToUserCartNow={onAddToUserCart}
          isInsuranceSelected={isInsuranceSelected}
        />
      </AccommodationSameRoomOptionTilePricing>
    </WalledContent>
  </AccommodationSameRoomOptionTile>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'offer'
  | 'rooms'
  | 'flightOrigin'
  | 'isLoggedIn'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  offer: state.bedbankOffer!,
  rooms: state.rooms,
  flightOrigin: state.airport,
  isLoggedIn: selectLoggedIn(appState),
}), BedbankMultiSameRoomRoomOption)
